import React, { useEffect, useState } from 'react';
import useAuthGuard from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import { API_BASE_URL } from '../config';
import apiFetch from '../utils/apiFetch';
import Pagination from '../components/Pagination';

const STORE_PLANS = ['free', 'standard', 'pro', 'enterprise'];
const MAKER_PLANS = ['trend', 'insight', 'enterprise'];

// Store portal account lifecycle. Applications arrive as pending and only
// become usable once approved here.
const STORE_STATUSES = ['pending', 'active', 'suspended', 'rejected'];

export default function B2BAccounts() {
  useAuthGuard();

  const [tab, setTab] = useState('store');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cursor, setCursor] = useState('');
  const [nextCursor, setNextCursor] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(null);
  const [cursorHistory, setCursorHistory] = useState([]);
  const [showIssue, setShowIssue] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      try {
        setLoading(true);
        setError('');
        const endpoint = tab === 'store' ? 'store-accounts' : 'maker-accounts';
        const url = new URL(`${API_BASE_URL}/admin/${endpoint}`);
        url.searchParams.set('limit', '20');
        if (cursor) url.searchParams.set('cursor', cursor);

        const res = await apiFetch(url.toString(), { signal: ac.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        const list = data.data?.items || data.items || data.data || [];
        setItems(Array.isArray(list) ? list : []);
        setNextCursor(data.meta?.cursor || '');
        setHasMore(data.meta?.hasMore ?? false);
        setTotal(data.meta?.total ?? null);
      } catch (e) {
        if (e.name !== 'AbortError') setError(e.message || 'Network Error');
      } finally {
        setLoading(false);
      }
    })();
    return () => ac.abort();
  }, [tab, cursor, reloadKey]);

  const switchTab = (t) => {
    setTab(t);
    setCursor('');
    setCursorHistory([]);
  };

  const handleNext = () => {
    if (nextCursor) {
      setCursorHistory((prev) => [...prev, cursor]);
      setCursor(nextCursor);
    }
  };

  const handlePrev = () => {
    setCursorHistory((prev) => {
      const next = [...prev];
      const prevCursor = next.pop();
      setCursor(prevCursor || '');
      return next;
    });
  };

  const handleStatusChange = async (item, newStatus) => {
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/store-accounts/${item.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, status: newStatus } : i)));
    } catch (e) {
      alert('Update failed: ' + e.message);
    }
  };

  const handlePlanChange = async (item, newPlan) => {
    const endpoint = tab === 'store' ? 'store-accounts' : 'maker-accounts';
    const body = tab === 'store'
      ? { plan: newPlan }
      : { plan: newPlan, companyName: item.companyName };
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/${endpoint}/${item.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, plan: newPlan } : i)));
    } catch (e) {
      alert('Update failed: ' + e.message);
    }
  };

  const handleDelete = async (item) => {
    const label = tab === 'store'
      ? (item.store?.name || item.id)
      : (item.companyName || item.id);
    if (!window.confirm(`「${label}」を削除しますか？`)) return;
    const endpoint = tab === 'store' ? 'store-accounts' : 'maker-accounts';
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/${endpoint}/${item.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setItems((prev) => prev.filter((i) => i.id !== item.id));
      setTotal((prev) => (prev != null ? prev - 1 : prev));
    } catch (e) {
      alert('Delete failed: ' + e.message);
    }
  };

  const fmtDate = (s) => {
    if (!s) return '-';
    const d = new Date(s);
    if (isNaN(d)) return s;
    return new Intl.DateTimeFormat('ja-JP', {
      timeZone: 'Asia/Tokyo', year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(d);
  };

  const plans = tab === 'store' ? STORE_PLANS : MAKER_PLANS;

  return (
    <Layout>
      <div className="mb-4 d-flex justify-content-between align-items-start">
        <div>
          <h2 className="page-title">B2B Account Management</h2>
          <p className="page-subtitle">Store / Maker accounts</p>
        </div>
        {tab === 'store' && (
          <button className="btn btn-primary btn-sm" onClick={() => setShowIssue((v) => !v)}>
            {showIssue ? '閉じる' : '店舗アカウント発行'}
          </button>
        )}
      </div>

      {tab === 'store' && showIssue && (
        <IssueStoreAccount
          onIssued={() => {
            setShowIssue(false);
            setCursor('');
            setCursorHistory([]);
            setReloadKey((k) => k + 1);
          }}
        />
      )}

      <ul className="nav nav-phoenix-tabs mb-3">
        <li className="nav-item">
          <button className={`nav-link ${tab === 'store' ? 'active' : ''}`} onClick={() => switchTab('store')}>
            Store Accounts
          </button>
        </li>
        <li className="nav-item">
          <button className={`nav-link ${tab === 'maker' ? 'active' : ''}`} onClick={() => switchTab('maker')}>
            Maker Accounts
          </button>
        </li>
      </ul>

      <div className="card">
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead className="table-light">
              <tr>
                {tab === 'store' ? (
                  <>
                    <th>Store</th>
                    <th>User</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Plan</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </>
                ) : (
                  <>
                    <th>Company</th>
                    <th>User</th>
                    <th>Plan</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="text-center py-4">Loading...</td></tr>
              )}
              {!loading && error && (
                <tr><td colSpan={7} className="text-danger py-4">{error}</td></tr>
              )}
              {!loading && !error && items.length === 0 && (
                <tr><td colSpan={7} className="text-center py-4" style={{ color: '#8a94ad' }}>No accounts found</td></tr>
              )}
              {!loading && !error && items.map((item) => (
                <tr key={item.id}>
                  {tab === 'store' ? (
                    <>
                      <td className="fw-medium">{item.store?.name || '-'}</td>
                      <td className="small">{item.user?.displayName || item.user?.email || '-'}</td>
                      <td><span className="badge bg-info">{item.role}</span></td>
                      <td>
                        <select
                          className="form-select form-select-sm"
                          value={item.status || 'active'}
                          onChange={(e) => handleStatusChange(item, e.target.value)}
                          style={{ width: 120, fontSize: '0.8125rem' }}
                        >
                          {STORE_STATUSES.map((st) => <option key={st} value={st}>{st}</option>)}
                        </select>
                      </td>
                      <td>
                        <select
                          className="form-select form-select-sm"
                          value={item.plan}
                          onChange={(e) => handlePlanChange(item, e.target.value)}
                          style={{ width: 120, fontSize: '0.8125rem' }}
                        >
                          {plans.map((p) => <option key={p} value={p}>{p}</option>)}
                        </select>
                      </td>
                      <td className="text-nowrap">{fmtDate(item.createdAt)}</td>
                      <td>
                        <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(item)} style={{ fontSize: '0.75rem' }}>
                          Delete
                        </button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="fw-medium">{item.companyName || '-'}</td>
                      <td className="small">{item.user?.displayName || item.user?.email || '-'}</td>
                      <td>
                        <select
                          className="form-select form-select-sm"
                          value={item.plan}
                          onChange={(e) => handlePlanChange(item, e.target.value)}
                          style={{ width: 130, fontSize: '0.8125rem' }}
                        >
                          {plans.map((p) => <option key={p} value={p}>{p}</option>)}
                        </select>
                      </td>
                      <td className="text-nowrap">{fmtDate(item.createdAt)}</td>
                      <td>
                        <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(item)} style={{ fontSize: '0.75rem' }}>
                          Delete
                        </button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="card-footer bg-white">
          <Pagination
            hasMore={hasMore}
            hasPrev={cursorHistory.length > 0}
            onNext={handleNext}
            onPrev={handlePrev}
            loading={loading}
            total={total}
            page={cursorHistory.length + 1}
            pageSize={20}
            itemCount={items.length}
          />
        </div>
      </div>
    </Layout>
  );
}


/**
 * Issue store portal credentials. The store owner signs in with these at
 * business.<domain> — they are not a public-site account, and the public
 * site's Firebase login will not accept them.
 */
function IssueStoreAccount({ onIssued }) {
  const [keyword, setKeyword] = useState('');
  const [stores, setStores] = useState([]);
  const [searching, setSearching] = useState(false);
  const [store, setStore] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [plan, setPlan] = useState('free');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const search = async () => {
    if (!keyword.trim()) return;
    setSearching(true);
    setError('');
    try {
      const url = new URL(`${API_BASE_URL}/admin/stores`);
      url.searchParams.set('q', keyword.trim());
      url.searchParams.set('limit', '10');
      const res = await apiFetch(url.toString());
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setStores(Array.isArray(data.data) ? data.data : []);
    } catch (e) {
      setError(e.message || 'Search failed');
    } finally {
      setSearching(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!store) {
      setError('店舗を選択してください');
      return;
    }
    setSaving(true);
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/store-accounts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          password,
          displayName,
          storeId: store.id,
          plan,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error?.message || `HTTP ${res.status}`);
      }
      onIssued();
    } catch (e) {
      setError(e.message || 'Failed to issue account');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card mb-3">
      <div className="card-body">
        <h5 className="mb-3">店舗アカウント発行</h5>
        {error && <div className="alert alert-danger py-2">{error}</div>}

        <form onSubmit={submit}>
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label small">店舗検索</label>
              <div className="input-group input-group-sm">
                <input
                  className="form-control"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="店舗名"
                />
                <button className="btn btn-outline-secondary" type="button" onClick={search} disabled={searching}>
                  {searching ? '...' : '検索'}
                </button>
              </div>
              {store && (
                <p className="small text-success mt-2 mb-0">選択中: {store.name}</p>
              )}
              {stores.length > 0 && (
                <ul className="list-group list-group-flush mt-2" style={{ maxHeight: 160, overflowY: 'auto' }}>
                  {stores.map((s) => (
                    <li key={s.id} className="list-group-item p-0">
                      <button
                        type="button"
                        className={`btn btn-sm w-100 text-start ${store?.id === s.id ? 'btn-light' : ''}`}
                        onClick={() => setStore(s)}
                      >
                        {s.name}
                        <span className="text-muted ms-2 small">{s.prefecture || ''}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="col-md-6">
              <div className="mb-2">
                <label className="form-label small">担当者名</label>
                <input
                  className="form-control form-control-sm"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  required
                />
              </div>
              <div className="mb-2">
                <label className="form-label small">メールアドレス（ログインID）</label>
                <input
                  type="email"
                  className="form-control form-control-sm"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="mb-2">
                <label className="form-label small">初期パスワード（8文字以上）</label>
                <input
                  className="form-control form-control-sm"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  minLength={8}
                  required
                />
              </div>
              <div>
                <label className="form-label small">プラン</label>
                <select
                  className="form-select form-select-sm"
                  value={plan}
                  onChange={(e) => setPlan(e.target.value)}
                >
                  {STORE_PLANS.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
            </div>
          </div>

          <button className="btn btn-primary btn-sm mt-3" type="submit" disabled={saving}>
            {saving ? '発行中...' : '発行する'}
          </button>
        </form>
      </div>
    </div>
  );
}
